import {
	applyActionCode,
	browserSessionPersistence,
	confirmPasswordReset,
	createUserWithEmailAndPassword,
	EmailAuthProvider,
	getMultiFactorResolver,
	multiFactor,
	reauthenticateWithCredential,
	reload,
	sendEmailVerification,
	sendPasswordResetEmail,
	setPersistence,
	signInWithEmailAndPassword,
	signOut,
	TotpMultiFactorGenerator,
	updatePassword,
} from "firebase/auth";
import type { MultiFactorError, TotpSecret } from "firebase/auth";
import {
	DEFAULT_AUTH_RETRY_DELAYS_MS,
	DEFAULT_BLOCKING_FUNCTION_ERRORS,
} from "./constants";
import { createAuthCleanup } from "./cleanup";
import {
	getAuthErrorLike,
	isBlockingTimeoutError,
	isRetryableBlockingError,
} from "./error-utils";
import { sleep } from "./helpers";
import type {
	CreateFirebaseAuthServiceOptions,
	FirebaseAuthService,
	ParsedError,
} from "./types";

export const createFirebaseAuthService = <TResponse extends ParsedError>(
	options: CreateFirebaseAuthServiceOptions<TResponse>
): FirebaseAuthService<TResponse> => {
	const {
		auth,
		projectName,
		parseError,
		successResponse,
		userNotFoundResponse,
		onPostSignUp,
		authRetryDelaysMs = DEFAULT_AUTH_RETRY_DELAYS_MS,
		blockingFunctionErrors = DEFAULT_BLOCKING_FUNCTION_ERRORS,
	} = options;

	const getCurrentUser = async () => auth.currentUser;
	const signOutUser = async () => signOut(auth);
	const onIdTokenChange = (onChange: (user: typeof auth.currentUser) => void) =>
		auth.onIdTokenChanged(onChange);
	const listenToAuthState = (onChange: (user: typeof auth.currentUser) => void) =>
		auth.onAuthStateChanged(onChange);

	const getIdTokenWithExpiry = async (forceRefresh = false) => {
		const user = await getCurrentUser();
		if (!user) {
			return null;
		}

		const tokenResult = await user.getIdTokenResult(forceRefresh);
		return {
			token: tokenResult.token,
			user,
			expirationTime: tokenResult.expirationTime,
		};
	};

	const setRememberMe = async (remember: boolean) => {
		if (!remember) {
			await setPersistence(auth, browserSessionPersistence);
		}
	};

	const { cleanupTransientAuthState, cleanupRecentCreatedUserByEmail } =
		createAuthCleanup(auth);

	const signInWithEmail: FirebaseAuthService<TResponse>["signInWithEmail"] = async (
		message,
		email,
		password,
		remember,
		l10n,
		signInOptions,
		retryCount = 0
	) => {
		try {
			const { user } = await signInWithEmailAndPassword(auth, email, password);
			await setRememberMe(remember);
			return user;
		} catch (err: unknown) {
			const authError = getAuthErrorLike(err);
			if (authError.code === "auth/multi-factor-auth-required") {
				throw err;
			}

			if (signInOptions?.onBlockingInternalError) {
				const handled = await signInOptions.onBlockingInternalError(authError);
				if (handled) {
					return;
				}
			}

			if (
				isRetryableBlockingError(authError, blockingFunctionErrors) &&
				retryCount < authRetryDelaysMs.length
			) {
				await cleanupTransientAuthState();
				await sleep(authRetryDelaysMs[retryCount]);
				return signInWithEmail(
					message,
					email,
					password,
					remember,
					l10n,
					signInOptions,
					retryCount + 1
				);
			}

			if (
				authError.code === "auth/user-not-found" ||
				authError.code === "auth/wrong-password"
			) {
				message.error(l10n("responses.auth/invalid-login-credentials"));
				return;
			}

			if (isBlockingTimeoutError(authError)) {
				await cleanupTransientAuthState();
				message.error(l10n("responses.auth/internal-error-encountered"));
				return;
			}

			const error = parseError(err);
			message.error(l10n(`responses.${error.code}`));
			return;
		}
	};

	const signUpWithEmail: FirebaseAuthService<TResponse>["signUpWithEmail"] = async (
		message,
		email,
		password,
		username,
		remember,
		subscribe,
		l10n,
		retryCount = 0
	) => {
		try {
			const { user } = await createUserWithEmailAndPassword(auth, email, password);
			if (user && onPostSignUp) {
				await onPostSignUp({ username, subscribe });
			}
			await setRememberMe(remember);
			return user;
		} catch (err: unknown) {
			const authError = getAuthErrorLike(err);
			if (
				isRetryableBlockingError(authError, blockingFunctionErrors) &&
				retryCount < authRetryDelaysMs.length
			) {
				await cleanupRecentCreatedUserByEmail(email);
				await cleanupTransientAuthState();
				await sleep(authRetryDelaysMs[retryCount]);
				return signUpWithEmail(
					message,
					email,
					password,
					username,
					remember,
					subscribe,
					l10n,
					retryCount + 1
				);
			}
			if (isBlockingTimeoutError(authError)) {
				await cleanupRecentCreatedUserByEmail(email);
				await cleanupTransientAuthState();
				message.error(l10n("responses.auth/internal-error-encountered"));
				return;
			}
			const error = parseError(err);
			message.error(l10n(`responses.${error.code}`));
			return;
		}
	};

	const isValidUser = (user: typeof auth.currentUser) => user?.uid != null;

	const sendUserPasswordResetEmail: FirebaseAuthService<TResponse>["sendUserPasswordResetEmail"] = async (
		message,
		email,
		l10n
	) => {
		try {
			await sendPasswordResetEmail(auth, email);
			message.success(l10n("responses.auth/password-reset-email-sent"));
			return successResponse;
		} catch (err: unknown) {
			const error = parseError(err);
			message.error(l10n(`responses.${error.code}`));
			return error;
		}
	};

	const resetPassword: FirebaseAuthService<TResponse>["resetPassword"] = async (
		message,
		code,
		password,
		l10n
	) => {
		try {
			await confirmPasswordReset(auth, code, password);
			message.success(l10n("responses.auth/password-reset-success"));
			return successResponse;
		} catch (err: unknown) {
			const error = parseError(err);
			message.error(l10n(`responses.${error.code}`));
			return error;
		}
	};

	const changePassword: FirebaseAuthService<TResponse>["changePassword"] = async (
		message,
		oldPassword,
		newPassword,
		l10n
	) => {
		try {
			const user = await getCurrentUser();
			if (user) {
				if (!user.email) {
					message.error(l10n("responses.auth/user-not-found"));
					return userNotFoundResponse;
				}
				const credential = EmailAuthProvider.credential(user.email, oldPassword);
				await reauthenticateWithCredential(user, credential);
				await updatePassword(user, newPassword);
				message.success(l10n("responses.auth/password-change-success"));
				return successResponse;
			}

			message.error(l10n("responses.auth/user-not-found"));
			return userNotFoundResponse;
		} catch (err: unknown) {
			const error = parseError(err);
			message.error(l10n(`responses.${error.code}`));
			return error;
		}
	};

	const requestSendEmailVerification: FirebaseAuthService<TResponse>["requestSendEmailVerification"] = async (
		message,
		l10n
	) => {
		try {
			const user = await getCurrentUser();
			if (user) {
				await sendEmailVerification(user);
				message.success(l10n("responses.auth/email-verification-sent"));
				return successResponse;
			}

			message.error(l10n("responses.auth/user-not-found"));
			return userNotFoundResponse;
		} catch (err: unknown) {
			const error = parseError(err);
			message.error(l10n(`responses.${error.code}`));
			return error;
		}
	};

	const verifyEmail: FirebaseAuthService<TResponse>["verifyEmail"] = async (
		message,
		code,
		l10n
	) => {
		try {
			await applyActionCode(auth, code);
			message.success(l10n("responses.auth/email-verification-success"));
			return true;
		} catch (err: unknown) {
			const error = parseError(err);
			message.error(l10n(`responses.${error.code}`));
			return false;
		}
	};

	const revert2fa: FirebaseAuthService<TResponse>["revert2fa"] = async (
		message,
		code,
		l10n
	) => {
		try {
			await applyActionCode(auth, code);
			message.success(l10n("responses.auth/2fa-revert-success"));
			return true;
		} catch (err: unknown) {
			const error = parseError(err);
			message.error(l10n(`responses.${error.code}`));
			return false;
		}
	};

	const generateMultiFactorSecret: FirebaseAuthService<TResponse>["generateMultiFactorSecret"] = async () => {
		const user = await getCurrentUser();
		if (!user) {
			return null;
		}
		const multiFactorSession = await multiFactor(user).getSession();
		const totpSecret = await TotpMultiFactorGenerator.generateSecret(
			multiFactorSession
		);
		return totpSecret;
	};

	const generateMultiFactorQrCode: FirebaseAuthService<TResponse>["generateMultiFactorQrCode"] = async (
		totpSecret
	) => {
		const user = await getCurrentUser();
		if (!user) {
			return null;
		}
		const secret = totpSecret as TotpSecret;
		let url = secret.generateQrCodeUrl(user.email || "", projectName);
		url = url.replace("&algorithm=HMACSHA1", "");
		return url;
	};

	const enrollMultiFactor: FirebaseAuthService<TResponse>["enrollMultiFactor"] = async (
		message,
		totpSecret,
		verificationCode,
		l10n
	) => {
		try {
			const user = await getCurrentUser();
			if (!user) {
				return false;
			}

			const multiFactorAssertion =
				TotpMultiFactorGenerator.assertionForEnrollment(
					totpSecret as TotpSecret,
					verificationCode
				);

			await multiFactor(user).enroll(multiFactorAssertion, "TOTP 2FA");
			return true;
		} catch (err: unknown) {
			const error = parseError(err);
			message.error(l10n(`responses.${error.code}`));
			return false;
		}
	};

	const unEnrollMultiFactor: FirebaseAuthService<TResponse>["unEnrollMultiFactor"] = async () => {
		const user = await getCurrentUser();
		if (!user) {
			return null;
		}
		const multiFactorInfo = multiFactor(user).enrolledFactors;
		const mfaEnrollmentId = multiFactorInfo[0].uid;
		await multiFactor(user).unenroll(mfaEnrollmentId);
		return;
	};

	const reAuthWithPassword: FirebaseAuthService<TResponse>["reAuthWithPassword"] = async (
		message,
		password,
		l10n
	) => {
		try {
			const user = await getCurrentUser();
			if (!user || !user.email) {
				return null;
			}
			const credential = EmailAuthProvider.credential(user.email, password);
			await reauthenticateWithCredential(user, credential);
			return true;
		} catch (err: unknown) {
			const error = parseError(err);
			if (error.code === "auth/multi-factor-auth-required") {
				throw err;
			}
			message.error(l10n(`responses.${error.code}`));
			return false;
		}
	};

	const checkIfMultiFactorAuthIsEnrolled: FirebaseAuthService<TResponse>["checkIfMultiFactorAuthIsEnrolled"] = async () => {
		const oldUser = await getCurrentUser();
		if (!oldUser) {
			return null;
		}
		await reload(oldUser);
		const user = await getCurrentUser();
		if (!user) {
			return null;
		}
		const multiFactorInfo = multiFactor(user).enrolledFactors;
		return multiFactorInfo.length > 0;
	};

	const resolveTOTPCode: FirebaseAuthService<TResponse>["resolveTOTPCode"] = async (
		message,
		code,
		error,
		l10n
	) => {
		try {
			const resolver = getMultiFactorResolver(auth, error as MultiFactorError);
			const selectedMFA = resolver.hints[0];
			if (selectedMFA.factorId === TotpMultiFactorGenerator.FACTOR_ID) {
				const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
					selectedMFA.uid,
					code
				);
				return resolver.resolveSignIn(multiFactorAssertion);
			}
			return undefined;
		} catch (err: unknown) {
			const parsedError = parseError(err);
			message.error(l10n(`responses.${parsedError.code}`));
			return false;
		}
	};

	return {
		getCurrentUser,
		getIdTokenWithExpiry,
		signOutUser,
		signInWithEmail,
		signUpWithEmail,
		isValidUser,
		listenToAuthState,
		onIdTokenChange,
		sendUserPasswordResetEmail,
		resetPassword,
		changePassword,
		requestSendEmailVerification,
		verifyEmail,
		generateMultiFactorSecret,
		generateMultiFactorQrCode,
		enrollMultiFactor,
		unEnrollMultiFactor,
		reAuthWithPassword,
		checkIfMultiFactorAuthIsEnrolled,
		resolveTOTPCode,
		revert2fa,
	};
};
